/* eslint-disable complexity */
import { filterTree, findNode, transformTree } from '@futrli/shared-services';

import { CATEGORY_NAME_INDEXES } from '../../../../core/constants';
import { ForecastTree, ForecastTreeNode } from '../types';
import { isTopLevelStaticRowType } from './utils';

/**
 * Will filter out the children of any collapsed nodes and then recalculate the siblingCount
 * and siblingIndex properties of each tree node in the given tree
 * @param tree - The tree of nodes to filter
 * @param expandedRows - The list of expanded row refs
 */
export const filterOutCollapsedTreeNodes = (
  tree: ForecastTreeNode[],
  expandedRows: (string | number)[]
): ForecastTreeNode[] => {
  let output = filterTree(tree, node => {
    return node.level === 0 || (!!node.parentRef && expandedRows.includes(node.parentRef));
  });

  output = transformTree(output, (node, level, parent, parentTree) => ({
    ...node,
    siblingCount: parent ? (parent.children || []).length : parentTree.length,
    siblingIndex: parent ? (parent.children || []).indexOf(node) : 0
  }));

  return output;
};

/**
 * Finds the top-level-static node for the given category in the provided tree
 * @param tree - The tree to search
 * @param category - The category to find the top level node for
 */
export const getTopLevelStaticCategoryTreeNode = (tree: ForecastTree, category: CATEGORY_NAME_INDEXES) => {
  return findNode(tree.tree, node => isTopLevelStaticRowType(node.type) && node.static_id === category);
};

/**
 * Updates all of the calculatable properties of each tree node in the given tree
 * @param tree - The tree nodes to update
 */
export const recalculateForecastTree = (tree: ForecastTreeNode[]): ForecastTreeNode[] => {
  return transformTree(tree, (node, level, parent, parentTree) => ({
    ...node,
    isFirst: level === 0 || (!!parent && parent.children?.[0] === node),
    isLast: !parent || (parent.children && parent.children[parent.children.length - 1] === node),
    level,
    parentRef: level !== 0 ? node.parentRef : undefined,
    siblingCount: parent ? (parent.children || []).length - 1 : parentTree.length - 1,
    siblingIndex: parent ? (parent.children || []).indexOf(node) : 0
  }));
};
